import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {useAuthInfo} from "@propelauth/react";
import {fetchWithToken, postWithToken} from "../../../api/apiBase";
import AddScopeDialog from "./AddScopeDialog";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "../../ui/table";
import {Button} from "../../ui/button";
import {Input} from "../../ui/input";
import BaseLoadingSpinner from "../../ui/baseLoadingSpinner";
import {moneyFormatter} from "../../../lib/formatters";
import {ProjectContext} from "../../../contexts/ProjectContext";
import {CompanyType} from "../../../models/company";
import {BidDeveloperData} from "../../bid/bidding_model";
import LinkInfo from "../../bid/developer/linkInfo";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "../../ui/dropdown-menu";
import {ChevronDown} from "lucide-react";
import MessageBiddersDialog from "./MessageBiddersDialog";
import InviteToQuickLink from "../../qlink/QuickLinkInvites";


type BidTabBid = {
    id: string
    bid_amount: number
    // developer only - leveled_amount is what the ranking is on
    developer?: BidDeveloperData | null
    company_name: string
    created_by?: string
    // rank change since bidders were last notified. negative = dropped.
    // null when there is no baseline for this company (a new bidder).
    rank_delta?: number | null
}

// from GET /projects/{id}/scopes/bid_tab - bids are latest per bidder, ranked lowest first
type ScopeBidTabRow = {
    id: string
    number: number
    title: string
    bids: BidTabBid[]
}

const ordinal = (n: number) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
}


/**
 * Bid tab - one row per scope, bidders ranked left to right by amount (lowest first).
 */
export default function ScopeOverview() {
    const {accessToken} = useAuthInfo();
    const {project_id} = useParams<string>();
    const projectContext = useContext(ProjectContext);
    const navigate = useNavigate();
    const [rows, setRows] = useState<ScopeBidTabRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    // scope id currently sending, and the last result to show the user
    const [sending, setSending] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    // dialog opened from a row's Actions menu, and which scope it is for
    const [dialog, setDialog] = useState<{kind: "email" | "invite", scopeId: string} | null>(null);

    const loadRows = useCallback(() => {
        if (!accessToken) return;
        setIsLoading(true);
        fetchWithToken(`/projects/${project_id}/scopes/bid_tab`, accessToken)
            .then((data) => {
                setRows(data);
                setError(null);
            }).catch(() => {
                setError("Error loading scope overview :(");
            }).finally(() => {
                setIsLoading(false);
            });
    }, [project_id, accessToken]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    /**
     * Email this scope's bidders that their position moved. The server
     * advances watch_state, so on reload the deltas for this row clear -
     * what is left showing is what bidders have not been told yet.
     */
    const notifyScope = (scopeId: string) => {
        if (!accessToken) return;
        setSending(scopeId);
        setNotice(null);
        postWithToken(`/projects/${project_id}/feedback/${scopeId}/notify`,
                      JSON.stringify({}), accessToken)
            .then((res) => {
                setNotice(res?.skipped
                    ? `Nothing sent: ${res.skipped}`
                    : `Sent ${res?.sent ?? 0} update${res?.sent === 1 ? "" : "s"}`);
                loadRows();
            })
            .catch(() => setNotice("Could not send updates"))
            .finally(() => setSending(null));
    };

    // same as directory search. matches the scope label as shown, or any bidder on it.
    // matched rows keep all their bids so the rank columns stay right.
    const [search, setSearch] = useState("");
    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((s) =>
            [`#${s.number} - ${s.title}`, ...s.bids.map((b) => b.company_name || b.created_by)]
                .some((v) => (v ?? "").toLowerCase().includes(q)));
    }, [rows, search]);

    if (projectContext?.role !== CompanyType.developer) return <></>;
    if (isLoading) return <div><BaseLoadingSpinner/></div>;
    if (error) return <div>{error}</div>;

    const numCols = Math.max(1, ...filteredRows.map((r) => r.bids.length));

    return (
        <Card>
            <CardHeader>
                <CardTitle>
                    <div className="flex justify-between items-center">
                        <h1 className="text-xm font-semibold">Scope Bid Tab</h1>
                        <div className="flex items-center gap-3">
                            {notice &&
                                <span className="text-xs font-normal text-gray-600">{notice}</span>
                            }
                            {/* font-normal - otherwise it picks up the CardTitle bold */}
                            <Input
                                className="w-64 font-normal"
                                placeholder="Search scope or bidder..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}/>
                            <AddScopeDialog onCreated={loadRows}/>
                            <Button variant="outline" onClick={() => navigate(`/project/${project_id}/scope_cards`)}>
                                Card View
                            </Button>
                        </div>
                    </div>
                </CardTitle>
            </CardHeader>
            <CardContent>
                {rows.length === 0
                    ? "No Scopes to show"
                    : filteredRows.length === 0
                    ? "No scopes match"
                    : <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Scope</TableHead>
                                {Array.from({length: numCols}, (_, i) =>
                                    <TableHead key={i} className="text-center">{ordinal(i + 1)}</TableHead>
                                )}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filteredRows.map((scope) => (
                                <TableRow key={scope.id}>
                                    <TableCell className="font-medium">
                                        {/* min width so the titles do not get squished when there are lots of bidders */}
                                        <div className="flex items-center justify-between gap-3 min-w-[24rem]">
                                            <Link className="hover:underline"
                                                  to={`/project/${project_id}/scopes/${scope.id}`}>
                                                #{scope.number} - {scope.title}
                                            </Link>
                                            <div className="flex flex-col gap-1">
                                            {/* a link - ctrl click keeps this page open */}
                                            <Button variant="outline" size="sm" asChild>
                                                <Link to={`/project/${project_id}/scopes/${scope.id}?tab=compare`}>
                                                    Compare
                                                </Link>
                                            </Button>
                                            {/* modal={false} - a modal menu opening a dialog can leave the page unclickable */}
                                            <DropdownMenu modal={false}>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" size="sm">
                                                        {sending === scope.id ? "Sending..." : "Actions"}
                                                        <ChevronDown className="ml-1 h-4 w-4 opacity-50"/>
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    {/* greyed out unless someone actually moved -
                                                        there is nothing to tell bidders otherwise */}
                                                    <DropdownMenuItem
                                                        disabled={sending !== null ||
                                                            !scope.bids.some((b) => b.rank_delta != null && b.rank_delta !== 0)}
                                                        onSelect={() => notifyScope(scope.id)}>
                                                        Send rank updates
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onSelect={() => setDialog({kind: "email", scopeId: scope.id})}>
                                                        Email bidders
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onSelect={() => setDialog({kind: "invite", scopeId: scope.id})}>
                                                        Invite bidders
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                            </div>
                                        </div>
                                    </TableCell>
                                    {Array.from({length: numCols}, (_, i) => {
                                        const bid = scope.bids[i];
                                        return (
                                            <TableCell key={i} className="text-center">
                                                {bid &&
                                                    <Link className="hover:underline inline-flex items-center gap-2"
                                                          to={`/project/${project_id}/bid/${bid.id}`}>
                                                        {/* rank delta to the left. fixed width so the amounts line up with or without one */}
                                                        <div className={`w-6 text-right text-xs font-semibold ${
                                                            (bid.rank_delta ?? 0) < 0 ? "text-red-600" : "text-green-600"}`}>
                                                            {bid.rank_delta != null && bid.rank_delta !== 0 &&
                                                                (bid.rank_delta > 0 ? `+${bid.rank_delta}` : bid.rank_delta)
                                                            }
                                                        </div>
                                                        <div>
                                                            {/* leveled amount is the main number (what the rank is on), bid amount secondary */}
                                                            <div className="font-semibold inline-flex items-center gap-1">
                                                                {moneyFormatter.format(bid.developer?.leveled_amount ?? bid.bid_amount)}
                                                                {bid.developer && <LinkInfo developer={bid.developer}/>}
                                                            </div>
                                                            <div className="text-xs text-gray-400">
                                                                bid {moneyFormatter.format(bid.bid_amount)}
                                                            </div>
                                                            <div className="text-xs text-gray-600">
                                                                {bid.company_name || bid.created_by}
                                                            </div>
                                                        </div>
                                                    </Link>
                                                }
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                }
                {/* the scope page dialogs, opened from a row's Actions menu.
                    keyed by scope so nothing carries over from another scope */}
                {dialog?.kind === "email" &&
                    <MessageBiddersDialog
                        key={dialog.scopeId}
                        scope_id={dialog.scopeId}
                        open={true}
                        onOpenChange={(open) => !open && setDialog(null)}/>
                }
                {dialog?.kind === "invite" &&
                    <InviteToQuickLink
                        key={dialog.scopeId}
                        scope_id={dialog.scopeId}
                        open={true}
                        onOpenChange={(open) => !open && setDialog(null)}/>
                }
            </CardContent>
        </Card>
    );
}
